import React, { memo, useCallback, useEffect, useState } from 'react';
import { ImageIcon } from '@saleshandy/design-system';
import CustomInput from '../../../../../../../shared/design-system/components/input/custom-input';
import StepWrapper from '../../../../../shared/stepwrapper';
import { CompanyInfoModalProps } from '../../../type';
import AiSequenceModalFooter from '../ai-sequence-modal-footer/ai-sequence-modal-footer';
import ChipTextInput from '../chip-input/chip-input';
import LabelDescriptionForm from '../label-description-modal/label-description-modal';
import {
  handleValidation,
  validateLabelAndDescriptionLength,
} from '../util/helper';
import { Images } from '../../../../../../../shared/app-constants';
import { truncate } from '../../../../../../../shared/utils';
import { OverlayTooltip } from '../../../../../../../shared/design-system/components/overlay';

/**
 *
 * @description
 * CompanyInfoModal is a React functional component that renders a modal for entering company information.
 * It includes fields for company name, description, industry, key services, and notable achievements.
 * The component uses state to manage the input values and references to handle editable fields.
 *
 * @param param0.handleMoveToNextStep - Function to handle moving to the next step
 * @returns {JSX.Element} react component
 */

const CompanyInfoModal: React.FC<CompanyInfoModalProps> = ({
  companyInfo = null,
  saveCurrentStepDetails = () => {},
}) => {
  const [companyInfoState, setCompanyInfoState] = useState({
    companyName: companyInfo?.name || '',
    description: companyInfo?.description || '',
    industryChips: companyInfo?.industry || [],
    serviceChips: companyInfo?.services || [],
    targetAudience: companyInfo?.targetAudience || '',
    achievements: companyInfo?.achievements || '', // ** TODO: Remove this and convert to array
    testimonials: companyInfo?.testimonials || '', // ** TODO: Remove this and convert to string
    isAchievementError: '',
    isTestmonialError: '',
    isAudienceError: '',
    isBtndisable: false,

    showValidationErrors: false,
    nameError: '',
    descError: '',
    isValidInput: false,
    isKeyServiceError: false,
    isIndustryError: false,
  });

  const validateLabelAndDescription = useCallback(
    (
      label: string,
      description: string,
      field: 'label' | 'description' | 'both' = 'both',
    ) => {
      const trimmedLabel = label?.trim();
      const trimmedDescription = description?.trim();

      const {
        isValid,
        labelError,
        descriptionError,
      } = validateLabelAndDescriptionLength({
        label: trimmedLabel,
        description: trimmedDescription,
        labelMinLength: 1,
        labelMaxLength: 200,
        descriptionMinLength: 10,
        descriptionMaxLength: 1000,
        labelErrorMsg: 'Company name must be',
        descriptionErrorMsg: 'Description must be',
        validateLabel: field === 'label' || field === 'both',
        validateDescription: field === 'description' || field === 'both',
      });

      setCompanyInfoState((prev) => ({
        ...prev,
        ...(field === 'label' || field === 'both'
          ? { nameError: labelError }
          : {}),
        ...(field === 'description' || field === 'both'
          ? { descError: descriptionError }
          : {}),
        isBtndisable: isValid,
      }));

      return isValid;
    },
    [],
  );

  const validateOtherFields = useCallback(() => {
    let isValid = true;

    const validateField = (
      value: string,
      field: 'isAchievementError' | 'isAudienceError' | 'isTestmonialError',
      min?: number,
      max?: number,
    ) => {
      const trimmedValue = value?.trim();
      const errorMsg = handleValidation(trimmedValue, () => {}, min, max);
      if (errorMsg) isValid = false;
      setCompanyInfoState((prev) => ({
        ...prev,
        [field]: errorMsg,
      }));
    };

    setCompanyInfoState((prev) => {
      const { achievements, targetAudience, testimonials } = prev;

      validateField(achievements, 'isAchievementError', 0, 1000);
      validateField(targetAudience, 'isAudienceError', 1, 1000);
      validateField(testimonials, 'isTestmonialError', 0, 1000);

      return prev;
    });

    return isValid;
  }, [companyInfoState]);

  const validateAll = useCallback(() => {
    const isLabelDescValid = validateLabelAndDescription(
      companyInfoState.companyName,
      companyInfoState.description,
    );
    const areOtherFieldsValid = validateOtherFields();

    const isKeyServiceValid = companyInfoState.serviceChips.length >= 3;
    const isIndustryValid = companyInfoState.industryChips.length >= 3;

    // Update error flags explicitly here
    setCompanyInfoState((prev) => ({
      ...prev,
      isKeyServiceError: !isKeyServiceValid,
      isIndustryError: !isIndustryValid,
      isValidInput:
        isLabelDescValid &&
        areOtherFieldsValid &&
        isKeyServiceValid &&
        isIndustryValid,
    }));

    return (
      isLabelDescValid &&
      areOtherFieldsValid &&
      isKeyServiceValid &&
      isIndustryValid
    );
  }, [
    validateLabelAndDescription,
    validateOtherFields,
    companyInfoState.companyName,
    companyInfoState.description,
    companyInfoState.serviceChips,
    companyInfoState.industryChips,
  ]);

  useEffect(() => {
    validateAll();
  }, [
    companyInfoState.companyName,
    companyInfoState.description,
    companyInfoState.achievements,
    companyInfoState.targetAudience,
    companyInfoState.testimonials,
    companyInfoState.industryChips,
    companyInfoState.serviceChips,
    companyInfoState.isKeyServiceError,
    companyInfoState.isIndustryError,
  ]);

  useEffect(() => {
    validateAll();
  }, []);

  const handleSubmit = useCallback(() => {
    setCompanyInfoState((prev) => ({
      ...prev,
      showValidationErrors: true,
    }));

    const isValid = validateAll();
    if (!isValid) return;

    const payload = {
      ...companyInfo,
      name: companyInfoState?.companyName?.trim(),
      description: companyInfoState?.description?.trim(),
      industry: companyInfoState?.industryChips,
      services: companyInfoState?.serviceChips,
      targetAudience: companyInfoState?.targetAudience?.trim(),
      achievements: companyInfoState?.achievements?.trim(),
      testimonials: companyInfoState?.testimonials?.trim(),
    };

    saveCurrentStepDetails({ companyInfo: payload });
  }, [
    saveCurrentStepDetails,
    companyInfoState,
    validateLabelAndDescription,
    validateAll,
  ]);

  return (
    <StepWrapper
      heading={
        <>
          {`Company Summary for ${truncate(companyInfo?.name, 26, false)}`}
          <div className="edit-info">
            <OverlayTooltip
              text="AI has generated this data, which might not be entirely accurate. Feel free to edit and improve it."
              className="company-details-tooltip"
            >
              <ImageIcon src={Images.InfoCircle} alt="info-circle" />
            </OverlayTooltip>
          </div>
        </>
      }
    >
      <div className="company-details-form">
        {/* Company Name and Description */}
        <LabelDescriptionForm
          nameLabel="Company Name"
          onNameChange={(e) =>
            setCompanyInfoState({
              ...companyInfoState,
              companyName: e?.target?.value,
            })
          }
          onDescriptionChange={(e) =>
            setCompanyInfoState({
              ...companyInfoState,
              description: e?.target?.value,
            })
          }
          onBlurCheck={true}
          showValidationError={companyInfoState.showValidationErrors}
          nameValue={companyInfoState?.companyName}
          descriptionValue={companyInfoState?.description}
          onValidate={validateLabelAndDescription}
          nameError={companyInfoState.nameError}
          descError={companyInfoState.descError}
          showMandatory={true}
        />

        {/* Key Services */}
        <div className="company-info-inputs">
          <p>Key Service*</p>
          <ChipTextInput
            value={companyInfoState?.serviceChips}
            onUpdateFilterConditionValue={(newChips) =>
              setCompanyInfoState((prev) => ({
                ...prev,
                serviceChips: newChips,
              }))
            }
            maxEntryLength={64}
            maxChip={10}
            className="company-info-chip-inputs"
            hasError={(error) =>
              setCompanyInfoState((prev) => ({
                ...prev,
                isKeyServiceError: error,
              }))
            }
          />
        </div>

        {/* Industry */}
        <div className="company-info-inputs">
          <p>Industry*</p>
          <ChipTextInput
            value={companyInfoState?.industryChips}
            onUpdateFilterConditionValue={(newChips) =>
              setCompanyInfoState((prev) => ({
                ...prev,
                industryChips: newChips,
              }))
            }
            maxEntryLength={64}
            className="company-info-chip-inputs"
            maxChip={10}
            hasError={(error) =>
              setCompanyInfoState((prev) => ({
                ...prev,
                isIndustryError: error,
              }))
            }
          />
        </div>

        {/* Notable Achievements/Stats */}
        <div className="company-info-inputs">
          <p>Notable Achievements/Stats</p>
          <CustomInput
            maxHeight={112}
            value={companyInfoState?.achievements}
            onChange={(e) => {
              const value = e?.target?.value;

              setCompanyInfoState((prev) => ({
                ...prev,
                achievements: value,
              }));
            }}
            className={`company-info-editable-input ${
              companyInfoState?.isAchievementError ? 'error' : ''
            }`}
          />
          {companyInfoState?.isAchievementError && (
            <span className="error-message">
              {companyInfoState?.isAchievementError}
            </span>
          )}
        </div>

        {/* Target Audience */}
        <div className="company-info-inputs">
          <p>Target Audience*</p>
          <CustomInput
            maxHeight={112}
            value={companyInfoState?.targetAudience}
            onChange={(e) => {
              const value = e?.target?.value;
              setCompanyInfoState((prev) => ({
                ...prev,
                targetAudience: value,
              }));
            }}
            className={`company-info-editable-input ${
              companyInfoState?.isAudienceError ? 'error' : ''
            }`}
          />
          {companyInfoState?.isAudienceError && (
            <span className="error-message">
              {companyInfoState?.isAudienceError}
            </span>
          )}
        </div>

        {/* Testimonials */}
        <div className="company-info-inputs">
          <p>Testimonials</p>
          <CustomInput
            maxHeight={112}
            value={companyInfoState?.testimonials}
            onChange={(e) => {
              const value = e?.target?.value;
              setCompanyInfoState((prev) => ({
                ...prev,
                testimonials: value,
              }));
            }}
            className={`company-info-editable-input ${
              companyInfoState?.isTestmonialError ? 'error' : ''
            }`}
          />
          {companyInfoState?.isTestmonialError && (
            <span className="error-message">
              {companyInfoState?.isTestmonialError}
            </span>
          )}
        </div>

        {/* Footer */}
        <AiSequenceModalFooter
          disableContinueBtn={false}
          // disableContinueBtn={companyInfoState.isValidInput}
          onClickContinue={() => handleSubmit()}
        />
      </div>
    </StepWrapper>
  );
};

export default memo(CompanyInfoModal);
